import React from 'react';
import { Modal, Box, Typography, LinearProgress, List, ListItem, ListItemText } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckIcon from '@material-ui/icons/Check';
import ErrorIcon from '@material-ui/icons/Error';

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    border: '2px solid #000',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    minWidth: 400,
    maxWidth: 600,
    maxHeight: '80vh',
    overflow: 'auto',
  },
  header: {
    marginBottom: theme.spacing(2),
  },
  listItem: {
    padding: theme.spacing(1, 0),
  },
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  progressBar: {
    flex: 1,
    marginRight: theme.spacing(1),
  },
  statusIcon: {
    width: 20,
    height: 20,
  },
  successIcon: {
    color: theme.palette.success.main,
  },
  errorIcon: {
    color: theme.palette.error.main,
  },
  progressText: {
    minWidth: 40,
    textAlign: 'right',
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.875rem',
  },
  closeButton: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 3),
    backgroundColor: '#0066cc',
    color: 'white',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#0052a3',
    },
    '&:disabled': {
      backgroundColor: '#cccccc',
      cursor: 'not-allowed',
    },
  },
}));

const UploadProgressModal = ({ 
  open, 
  onClose, 
  files = [], 
  uploadProgress = {},
  uploadResults = {},
  isCompleted = false 
}) => {
  const classes = useStyles();

  const getStatusIcon = (filename) => {
    const result = uploadResults[filename];
    if (result) {
      if (result.success) {
        return <CheckIcon className={`${classes.statusIcon} ${classes.successIcon}`} />;
      } else {
        return <ErrorIcon className={`${classes.statusIcon} ${classes.errorIcon}`} />;
      }
    }
    return null;
  };

  const getProgressValue = (filename) => {
    return uploadProgress[filename] || 0;
  };

  const getStatusText = (filename) => {
    const result = uploadResults[filename];
    const progress = getProgressValue(filename);
    
    if (result) {
      if (result.success) {
        return 'Completed';
      } else {
        return 'Failed';
      }
    } else if (progress > 0) {
      return `${progress}%`;
    }
    return 'Waiting...';
  };

  const getErrorMessage = (filename) => {
    const result = uploadResults[filename];
    if (result && !result.success) {
      return result.error || 'Upload failed';
    }
    return null;
  };

  // Translations (you can expand this based on your i18n setup)
  const translations = {
    en: {
      title: 'Upload Progress',
      close: 'Close',
      completed: 'Completed',
      failed: 'Failed',
      waiting: 'Waiting...',
    },
    de: {
      title: 'Upload-Fortschritt',
      close: 'Schließen',
      completed: 'Abgeschlossen',
      failed: 'Fehlgeschlagen',
      waiting: 'Warten...',
    }
  };

  // For now using English, you can integrate with your i18n system
  const t = translations.en;

  return (
    <Modal
      open={open}
      onClose={isCompleted ? onClose : undefined}
      className={classes.modal}
      disableBackdropClick={!isCompleted}
      disableEscapeKeyDown={!isCompleted}
    >
      <Box className={classes.paper}>
        <Typography variant="h6" component="h2" className={classes.header}>
          {t.title}
        </Typography>
        
        <List>
          {files.map((file, index) => {
            const filename = file.name;
            const progress = getProgressValue(filename);
            const statusIcon = getStatusIcon(filename);
            const statusText = getStatusText(filename);
            const errorMessage = getErrorMessage(filename);
            
            return (
              <ListItem key={index} className={classes.listItem}>
                <ListItemText
                  primary={filename}
                  secondary={
                    <div>
                      <div className={classes.progressContainer}>
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          className={classes.progressBar}
                        />
                        {statusIcon}
                        <Typography variant="body2" className={classes.progressText}>
                          {statusText}
                        </Typography>
                      </div>
                      {errorMessage && (
                        <Typography className={classes.errorText}>
                          {errorMessage}
                        </Typography>
                      )}
                    </div>
                  }
                />
              </ListItem>
            );
          })}
        </List>
        
        {isCompleted && (
          <button
            className={classes.closeButton}
            onClick={onClose}
          >
            {t.close}
          </button>
        )}
      </Box>
    </Modal>
  );
};

export default UploadProgressModal;